import React from "react";

export const KW_OPPORTUNITIES_TYPE = {
  NONE: 'NONE',
  OUTDATED: 'OUTDATED',
  MISSING_WORDS: 'MISSING_WORDS',
} as const;

export type CategorizationType = keyof typeof KW_OPPORTUNITIES_TYPE;

export const KW_OPPORTUNITIES_MAP: Record<
  CategorizationType,
  {
    label: () => React.ReactNode;
    filterItemLabel: string;
    tooltip: (params?: { missingWords?: string }) => JSX.Element;
  }
> = {
  [KW_OPPORTUNITIES_TYPE.OUTDATED]: {
    label: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 384 512">
        <path fill="#7C7D80" d="M32 0C14.3 0 0 14.3 0 32S14.3 64 32 64l0 11c0 42.4 16.9 83.1 46.9 113.1L146.7 256 78.9 323.9C48.9 353.9 32 394.6 32 437l0 11c-17.7 0-32 14.3-32 32s14.3 32 32 32l32 0 256 0 32 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l0-11c0-42.4-16.9-83.1-46.9-113.1L237.3 256l67.9-67.9c30-30 46.9-70.7 46.9-113.1l0-11c17.7 0 32-14.3 32-32s-14.3-32-32-32L320 0 64 0 32 0zM288 437l0 11L96 448l0-11c0-25.5 10.1-49.9 28.1-67.9L192 301.3l67.9 67.9c18 18 28.1 42.4 28.1 67.9z" />
      </svg>
    ),
    filterItemLabel: 'Outdated',
    tooltip: () => (
      <>
        <strong>Outdated Pages.</strong> Top 5 results for this keyword contain pages over 6 months old. Google prefers fresh and relevant content.
        <br />
        <br />
        <strong>Action:</strong> Update your page or create a new one to outrank them.
      </>
    ),
  },
  [KW_OPPORTUNITIES_TYPE.NONE]: {
    label: () => "-",
    filterItemLabel: 'None',
    tooltip: () => (
      <>
        <strong>No Weak Spots Found.</strong> We haven't found any weak spots in the TOP 5 ranking URLs.
      </>
    ),
  },
  [KW_OPPORTUNITIES_TYPE.MISSING_WORDS]: {
    label: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 384 512">
        <path fill="#7C7D80" d="M40.1 467.1l-11.2 9c-3.2 2.5-7.1 3.9-11.1 3.9C8 480 0 472 0 462.2L0 192C0 86 86 0 192 0S384 86 384 192l0 270.2c0 9.8-8 17.8-17.8 17.8c-4 0-7.9-1.4-11.1-3.9l-11.2-9c-13.4-10.7-32.8-9-44.1 3.9L269.3 506c-3.3 3.8-8.2 6-13.3 6s-9.9-2.2-13.3-6l-26.6-30.5c-12.7-14.6-35.4-14.6-48.2 0L141.3 506c-3.3 3.8-8.2 6-13.3 6s-9.9-2.2-13.3-6L84.2 471c-11.3-12.9-30.7-14.6-44.1-3.9zM160 192a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 32a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" />
      </svg>
    ),
    filterItemLabel: 'Missing words in the titles',
    tooltip: ({ missingWords } = { missingWords: '' }) => (
      <>
        <strong>Missing Title Words.</strong> Top 5 results for this keyword contain pages that don't have all words of the keyword in their meta titles. <strong>Missing:</strong> {missingWords || ''}.
        <br />
        <br />
        <strong>Action:</strong> Add the keyword to your meta title using all exact words in the same order to outrank these pages.
      </>
    ),
  },
};

export const KW_OPPORTUNITIES_FILTER_MAP: Record<
  CategorizationType,
  {
    label: () => React.ReactNode;
    filterItemLabel: string;
    tooltip: (params?: { missingWords?: string }) => JSX.Element;
  }
> = {
  [KW_OPPORTUNITIES_TYPE.OUTDATED]: {
    label: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 384 512">
        <path fill="#7C7D80" d="M32 0C14.3 0 0 14.3 0 32S14.3 64 32 64l0 11c0 42.4 16.9 83.1 46.9 113.1L146.7 256 78.9 323.9C48.9 353.9 32 394.6 32 437l0 11c-17.7 0-32 14.3-32 32s14.3 32 32 32l32 0 256 0 32 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l0-11c0-42.4-16.9-83.1-46.9-113.1L237.3 256l67.9-67.9c30-30 46.9-70.7 46.9-113.1l0-11c17.7 0 32-14.3 32-32s-14.3-32-32-32L320 0 64 0 32 0zM288 437l0 11L96 448l0-11c0-25.5 10.1-49.9 28.1-67.9L192 301.3l67.9 67.9c18 18 28.1 42.4 28.1 67.9z" />
      </svg>
    ),
    filterItemLabel: 'Outdated',
    tooltip: () => (
      <>
        <strong>Outdated Pages.</strong> Find keywords with pages in the TOP 5 results of a SERP that had not been updated for more than 6 months. Google often downranks such content if it finds other, more fresh, but also relevant pages.
        <br />
        <br />
        <strong>Action:</strong> Use this as an opportunity to rank in the TOP 5.
      </>
    ),
  },
  [KW_OPPORTUNITIES_TYPE.NONE]: {
    label: () => "-",
    filterItemLabel: 'None',
    tooltip: () => (
      <>
        <strong>No Weak Spots Found.</strong> We haven't found any weak spots in the TOP 5 ranking URLs.
      </>
    ),
  },
  [KW_OPPORTUNITIES_TYPE.MISSING_WORDS]: {
    label: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 384 512">
        <path fill="#7C7D80" d="M40.1 467.1l-11.2 9c-3.2 2.5-7.1 3.9-11.1 3.9C8 480 0 472 0 462.2L0 192C0 86 86 0 192 0S384 86 384 192l0 270.2c0 9.8-8 17.8-17.8 17.8c-4 0-7.9-1.4-11.1-3.9l-11.2-9c-13.4-10.7-32.8-9-44.1 3.9L269.3 506c-3.3 3.8-8.2 6-13.3 6s-9.9-2.2-13.3-6l-26.6-30.5c-12.7-14.6-35.4-14.6-48.2 0L141.3 506c-3.3 3.8-8.2 6-13.3 6s-9.9-2.2-13.3-6L84.2 471c-11.3-12.9-30.7-14.6-44.1-3.9zM160 192a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 32a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" />
      </svg>
    ),
    filterItemLabel: 'Missing words in the titles',
    tooltip: ({ missingWords } = { missingWords: '' }) => (
      <>
        <strong>Pages with missing keywords in the meta title.</strong> We found pages in the TOP 5 results of a SERP that don't contain all keywords in their meta titles. <strong>Missing:</strong> {missingWords || ''}.
        <br />
        <br />
        <strong>Action:</strong> Use this as an opportunity to rank in the TOP 5.
      </>
    ),
  },
};
